import React, { useMemo, useEffect, useState, useRef } from "react";
import {
  GoogleMap,
  Marker,
  DirectionsRenderer,
  useJsApiLoader,
} from "@react-google-maps/api";
import { MapAPIKey } from "../../Utility/Utility";
import Styles from "./MapContainer.module.css";
import CenterPin from "../../Assets/map_marker.svg";
import toast from "react-hot-toast";
import Loader from "../Loader/Loader";

const LIBRARIES = ["places"];

function MapContainer({
  origin,
  location,
  showDirections = false,
  showCurrentLocation = false,
  isEdit = false,
  onSelectLocation,
}) {
  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: MapAPIKey,
    libraries: LIBRARIES,
  });

  const mapRef = useRef(null);

  const [directions, setDirections] = useState(null);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [centerMarker, setCenterMarker] = useState(null);
  const [loading, setLoading] = useState(false);

  /* ---------------- DESTINATION ---------------- */

  const destination = useMemo(
    () =>
      location
        ? { lat: Number(location.lat), lng: Number(location.lng) }
        : null,
    [location],
  );

  const center = useMemo(
    () => origin ?? destination ?? { lat: 11.0168, lng: 76.9558 },
    [origin, destination],
  );

  /* ---------------- DIRECTIONS ---------------- */

  useEffect(() => {
    if (!showDirections || !origin || !destination) {
      setDirections(null);
      return;
    }

    if (!isLoaded) return;

    const directionsService = new window.google.maps.DirectionsService();

    directionsService.route(
      {
        origin,
        destination,
        travelMode: window.google.maps.TravelMode.DRIVING,
      },
      (result, status) => {
        if (status === "OK") {
          setDirections(result);
        } else {
          console.error("Directions failed:", status);
          setDirections(null);
        }
      },
    );
  }, [isLoaded, origin, destination, showDirections]);

  /* ---------------- REVERSE GEOCODE ---------------- */

  const getAddressFromLatLng = async (lat, lng) => {
    const geocoder = new window.google.maps.Geocoder();

    return new Promise((resolve, reject) => {
      geocoder.geocode(
        { location: { lat: Number(lat), lng: Number(lng) } },
        (results, status) => {
          if (status === "OK" && results.length > 0) {
            resolve(results[0].formatted_address);
          } else {
            reject(status);
          }
        },
      );
    });
  };

  /* ---------------- CURRENT LOCATION ---------------- */

  const fetchCurrentLocation = () => {
    if (!navigator.geolocation) return;
    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;

        const loc = { lat, lng };

        setCurrentLocation(loc);

        if (mapRef.current) {
          mapRef.current.panTo(loc);
        }

        try {
          const addr = await getAddressFromLatLng(lat, lng);

          onSelectLocation?.({
            lat,
            lng,
            address: addr,
          });
        } catch (err) {
          console.error(err);
        } finally {
          setLoading(false);
        }
      },
      (err) => {
        toast(err.message, { type: "error" });
        setLoading(false);
      },
    );
  };

  /* ---------------- MAP DRAG ---------------- */

  const handleMapIdle = () => {
    if (!isEdit || !mapRef.current) return;

    const center = mapRef.current.getCenter();

    setCenterMarker({
      lat: center.lat(),
      lng: center.lng(),
    });
  };

  /* ---------------- CONFIRM LOCATION ---------------- */

  const confirmLocation = async () => {
    if (!centerMarker) return;

    try {
      setLoading(true);

      const addr = await getAddressFromLatLng(
        centerMarker.lat,
        centerMarker.lng,
      );

      onSelectLocation?.({
        lat: centerMarker.lat,
        lng: centerMarker.lng,
        address: addr,
      });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (!isLoaded) return null;

  return (
    <div className={Styles.mapWrapper}>
      {/* LOADER */}
      {loading && <Loader isLoader={loading} />}
      {/* {loading && <div className={Styles.mapLoader}>Fetching location...</div>} */}

      <GoogleMap
        mapContainerClassName={Styles.mapContainer}
        center={center}
        zoom={16}
        onLoad={(map) => (mapRef.current = map)}
        onIdle={handleMapIdle}
      >
        {/* Destination marker */}
        {destination && !isEdit && <Marker position={destination} />}

        {/* Origin marker */}
        {origin && !isEdit && <Marker position={origin} />}

        {/* Current location marker */}
        {currentLocation && !isEdit && <Marker position={currentLocation} />}

        {/* Directions */}
        {showDirections && origin && directions && (
          <DirectionsRenderer
            directions={directions}
            options={{
              suppressMarkers: true,
              polylineOptions: {
                strokeColor: "#2684ff",
                strokeWeight: 5,
              },
            }}
          />
        )}
      </GoogleMap>

      {/* CENTER MARKER */}
      {isEdit && (
        <div className={Styles.centerMarker}>
          <img src={CenterPin} alt="Select location" />
        </div>
      )}

      {/* CURRENT LOCATION BUTTON */}
      {showCurrentLocation && isEdit && (
        <button
          className={Styles.currentLocationBtn}
          onClick={fetchCurrentLocation}
        >
          📍
        </button>
      )}

      {/* CONFIRM BUTTON */}
      {isEdit && centerMarker && (
        <button className={Styles.confirmBtn} onClick={confirmLocation}>
          ✔
        </button>
      )}
    </div>
  );
}

export default React.memo(MapContainer);
